import {apiDocsPlugin, ApiExplorerPage} from '@backstage/plugin-api-docs';
import {CatalogImportPage, catalogImportPlugin,} from '@backstage/plugin-catalog-import';
import {dtLightTheme} from './theme/light';
import {dtDarkTheme} from './theme/dark';
import {
    dtDuskPurpleTheme,
    dtForestTheme,
    dtHackerTerminalTheme,
    dtHighContrastLightTheme,
    dtOceanBlueTheme,
    dtSlateDimmedTheme,
    dtSunriseTheme,
    dtWarmSandTheme,
} from './theme/themes';

import {AlertDisplay, OAuthRequestDialog, SignInPage,} from '@backstage/core-components';

import {RequirePermission} from '@backstage/plugin-permission-react';
import {catalogEntityCreatePermission} from '@backstage/plugin-catalog-common/alpha';
import {NotificationsPage} from '@backstage/plugin-notifications';
import {SignalsDisplay} from '@backstage/plugin-signals';

import {configApiRef, githubAuthApiRef, useApi,} from '@backstage/core-plugin-api';
import {AppRouter, FlatRoutes} from '@backstage/core-app-api';

import {CatalogEntityPage, CatalogIndexPage, catalogPlugin,} from '@backstage/plugin-catalog';
import {CatalogGraphPage} from '@backstage/plugin-catalog-graph';
import {Route} from 'react-router';
import {TechDocsIndexPage, techdocsPlugin, TechDocsReaderPage,} from '@backstage/plugin-techdocs';
import {LightBox, ReportIssue, TextSize} from '@backstage/plugin-techdocs-module-addons-contrib';
import {UnifiedThemeProvider} from '@backstage/theme';

import {Root} from './components/Root';
import {SearchPage} from '@backstage/plugin-search';
import {CustomSettingsPage} from './components/settings/CustomSettingsPage';
import {apis} from './apis';
import {createApp} from '@backstage/app-defaults';
import {entityPage} from './components/catalog/EntityPage';
import {HomePage} from './components/catalog/HomePage';
import {orgPlugin} from '@backstage/plugin-org';
import {searchPage} from './components/search/SearchPage';
import {ScaffolderPage, scaffolderPlugin} from '@backstage/plugin-scaffolder';
import {TechDocsAddons} from '@backstage/plugin-techdocs-react';
import {TechDocsThemeAddon} from './components/techdocs/TechDocsThemeAddon';
import {HomepageCompositionRoot} from '@backstage/plugin-home';
import {CssBaseline} from '@material-ui/core';

import {DevToolsPage} from '@backstage/plugin-devtools';
import {CatalogUnprocessedEntitiesPage} from '@backstage/plugin-catalog-unprocessed-entities';
import {customDevToolsPage} from "./components/devtools/CustomDevToolsPage";

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  themes: [
    {
      id: 'light',
      title: 'Light',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={dtLightTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'dark',
      title: 'Dark',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={dtDarkTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'high-contrast-light',
      title: 'High Contrast',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={dtHighContrastLightTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'warm-sand',
      title: 'Warm Sand',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={dtWarmSandTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'ocean-blue',
      title: 'Ocean Blue',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={dtOceanBlueTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'slate-dimmed',
      title: 'Slate Dimmed',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={dtSlateDimmedTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'hacker-terminal',
      title: 'Hacker Terminal',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={dtHackerTerminalTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'dusk-purple',
      title: 'Dusk Purple',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={dtDuskPurpleTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'sunrise',
      title: 'Sunrise',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={dtSunriseTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'forest',
      title: 'Forest',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={dtForestTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
  ],
  components: {
    SignInPage: props => {
      const configApi = useApi(configApiRef);
      if (configApi.getString('auth.environment') === 'local') {
        return (
          <SignInPage
            {...props}
            providers={[
              'guest',
              {
                id: 'github-auth-provider',
                title: 'GitHub',
                message: 'Sign in using GitHub',
                apiRef: githubAuthApiRef,
              },
            ]}
          />
        );
      }
      return (
        <SignInPage
          {...props}
          provider={{
            id: 'github-auth-provider',
            title: 'GitHub',
            message: 'Sign in using GitHub',
            apiRef: githubAuthApiRef,
          }}
        />
      );
    },
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <TechDocsThemeAddon />
        <TextSize />
        <ReportIssue />
        <LightBox />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<CustomSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/devtools" element={<DevToolsPage />} >
        {customDevToolsPage}
    </Route>
    <Route path="/catalog-unprocessed-entities" element={<CatalogUnprocessedEntitiesPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay transientTimeoutMs={2500} />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
